import {
    Dialog,
    DialogTitle,
    Stack,
    IconButton,
    Typography,
    DialogContent,
    TextField,
    Grid,
    useMediaQuery,
    Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import EditIcon from "@mui/icons-material/Edit";
import { EditKategoriBody, ErrorFieldResponse } from "../../constants/types";
import Swal from "sweetalert2";
import { LoadingButton } from "@mui/lab";
import defaultAxios, { AxiosError } from "axios";
import useUpdateKategori from "../../services/mutations/useUpdateKategori";

interface IModalEditKategoriProps {
    refetch: () => void;
    onBackToKategori: () => void;
    open: boolean;
    onClose: () => void;
    kategoriName: string;
    kategoriId: number | string | undefined;
}

// interface IValuesKategori {
//   id: number | string | undefined;
//   nama: string;
// }

const schema = yup
    .object({
        namaKategori: yup.string().required("Kolom wajib diisi"),
    })
    .required();

const ModalEditKategori = ({
    refetch,
    onBackToKategori,
    open,
    onClose,
    kategoriName,
    kategoriId,
}: IModalEditKategoriProps) => {
    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.between("xs", "sm"));
    const updateKategori = useUpdateKategori();

    const initialValues = useMemo(
        () => ({
            // id: idKategori,
            namaKategori: kategoriName,
        }),
        [kategoriName],
    );

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
        getValues,
        setError,
    } = useForm<EditKategoriBody>({
        resolver: yupResolver(schema),
        defaultValues: initialValues,
    });

    const onSubmit = (values: EditKategoriBody) => {
        if (kategoriId === undefined) {
            onClose();
            onBackToKategori();
            return;
        }
        updateKategori.mutate(
            { id: kategoriId, namaKategori: values.namaKategori },
            {
                onSuccess: (data) => {
                    if (data.code === 200) {
                        Swal.fire({
                            title:
                                "Kategori berhasil diubah menjadi " +
                                getValues("namaKategori"),
                            position: "top-end",
                            showConfirmButton: false,
                            icon: "success",
                            toast: true,
                            timer: 3000,
                            timerProgressBar: true,
                            showCloseButton: true,
                            customClass: {
                                container: "my-swal",
                            },
                        });
                    }
                    refetch();
                    onClose();
                    onBackToKategori();
                },
                onError: (error) => {
                    if (defaultAxios.isAxiosError(error)) {
                        const serverError = error as AxiosError<
                            ErrorFieldResponse | undefined
                        >;
                        if (serverError?.response) {
                            const fieldError = serverError.response.data;
                            if (fieldError?.errors) {
                                Object.keys(fieldError.errors).forEach(
                                    (key) => {
                                        const errorMessage =
                                            fieldError.errors[key];
                                        setError(
                                            key as keyof typeof initialValues,
                                            {
                                                type: "manual",
                                                message: errorMessage[0],
                                            },
                                        );
                                    },
                                );
                            }
                        }
                    }
                    console.error(error);
                },
            },
        );
    };

    useEffect(() => {
        if (open) {
            if (kategoriName && kategoriId) {
                reset(initialValues);
                // console.log(initialValues);
            }
        }
    }, [kategoriId, initialValues, open, kategoriName, reset]);

    return (
        <Dialog
            maxWidth="xs"
            fullWidth={true}
            fullScreen={isPhoneScreen}
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { borderRadius: 1 },
            }}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                >
                    <Typography variant="h6" fontWeight="bold">
                        Edit Kategori
                    </Typography>

                    <IconButton
                        onClick={() => {
                            onClose();
                            onBackToKategori();
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={1}>
                        <Grid item xs={8} sm={9}>
                            <Controller
                                name="namaKategori"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="nama"
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        error={Boolean(errors.namaKategori)}
                                        helperText={
                                            errors.namaKategori
                                                ? errors.namaKategori.message
                                                : " "
                                        }
                                        {...field}
                                    />
                                )}
                                rules={{ required: "Nama barang required" }}
                            />
                        </Grid>
                        <Grid item xs={4} sm={3}>
                            <LoadingButton
                                loading={updateKategori.isLoading}
                                loadingPosition="center"
                                variant="contained"
                                fullWidth
                                size="large"
                                type="submit"
                                onClick={handleSubmit(onSubmit)}
                                sx={{ top: 8, height: "40px" }}
                            >
                                {isPhoneScreen ? <EditIcon /> : "Simpan"}
                            </LoadingButton>
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default ModalEditKategori;
